import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { DownArrowIcon } from '../../../components/icon/icon';
import { DairySearchCondition } from '../../../components/common/common.dairy.search.condition';
import { DairyItems } from '../../../components/user/dairy/user.dairy.section';
import { DairyTypeModal } from '../../../components/user/dairy/modal/modal.dairy.type.select';
import { DairyStatementModal } from '../../../components/user/dairy/modal/modal.dairy.statement.regist';
import { fetchReportList } from '../../../api/report';

interface SearchConditionProps {
  name: string;
  startDate: string;
  endDate: string;
  monthValue: string;
  reportType: string;
  searchText: string;
}

interface ReportDto {
  id: number; // 리포트 ID
  arrivedAt: string; // 도착 날짜
  name: string; // 회사 이름
  dayType: string; // 작업 유형 (반일, 하루 등)
  reportType: string; // 리포트 유형 (belonged 등)
}

interface CompanyReportDto {
  totalAmount: number; // 총 금액
  reports: ReportDto[]; // 리포트 배열
}

const UserDiary = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [dairyType, setDairyType] = useState(false);
  const [statementOpen, setStatementOpen] = useState(false);

  // data state
  const [loading, setLoading] = useState(true); // 로딩 상태 관리
  const [dataList, setDataList] = useState<CompanyReportDto | null>(null); // null로 초기화하여 빈 상태 고려

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const [searchCondition, setSearchCondition] = useState<SearchConditionProps>({
    name: '',
    endDate: '',
    monthValue: '',
    reportType: '',
    searchText: '',
    startDate: '',
  });
  const handleSearchClick = () => {
    setSearchOpen(true);
  };
  const handleSearchClose = () => {
    setSearchOpen(false);
  };
  const handleSearchSubmit = (data: SearchConditionProps) => {
    console.log('검색 조건', data);
    setSearchCondition(data);
    setSearchOpen(false);
  };
  const handleDairyTypeClick = () => {
    setDairyType(true);
  };
  const handleDairyTypeClose = () => {
    setDairyType(false);
  };
  const handleStatementClick = () => {
    setStatementOpen(true);
  };
  const handleStatementClose = () => {
    setStatementOpen(false);
  };

  // 데이터 가져오기
  useEffect(() => {
    const getReportList = async () => {
      try {
        setLoading(true);

        const { startDate, endDate, name, reportType, searchText } =
          searchCondition;

        // 날짜 형식 변경 함수 (YYYY-MM-DD)
        const formatDate = (date: string) => {
          const dateObj = new Date(date);
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const today = new Date();
        const firstDayOfMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          1,
        );

        const startDateString =
          startDate || formatDate(firstDayOfMonth.toISOString()); // 월의 첫 번째 날로 설정
        const endDateString = endDate || formatDate(today.toISOString()); // 오늘 날짜로 설정

        const reportTypeForSearch =
          reportType === 'belonged,rent' ? '' : reportType;

        // API 호출 시 검색 조건 포함
        const response = await fetchReportList({
          startDate: startDateString,
          endDate: endDateString,
          name,
          reportType: reportTypeForSearch,
        });

        setDataList(response); // API 호출 결과를 상태에 저장
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    getReportList();
  }, [searchCondition]); // 검색 조건이 변경될 때마다 실행

  return (
    <Container>
      <SearchForm onClick={handleSearchClick}>
        <span>
          {searchCondition.startDate && searchCondition.endDate
            ? `${searchCondition.startDate} ~ ${searchCondition.endDate}`
            : '검색'}
        </span>
        <div className="icon-box">
          <DownArrowIcon />
        </div>
      </SearchForm>
      {dataList ? ( // dataList가 존재할 때만 DairyItems 렌더링
        <DairyItems
          totalAmount={dataList.totalAmount} // totalAmount 전달
          reports={dataList.reports} // reports 전달
          registClick={handleDairyTypeClick}
          statementClick={handleStatementClick}
        />
      ) : (
        <p>데이터를 불러오는 중입니다...</p> // 로딩 상태 표시
      )}
      <AnimatePresence initial={false}>
        {searchOpen ? (
          <SearchConditionWrap
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              type: 'fade',
            }}
          >
            <DairySearchCondition
              handleSearchClose={handleSearchClose}
              handleSearchSubmit={handleSearchSubmit}
            />
          </SearchConditionWrap>
        ) : null}
        {dairyType ? (
          <DairyTypeModal handleConfirmClose={handleDairyTypeClose} />
        ) : null}
        {statementOpen ? (
          <SearchConditionWrap
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              type: 'fade',
            }}
          >
            <DairyStatementModal handleConfirmClose={handleStatementClose} />
          </SearchConditionWrap>
        ) : null}
      </AnimatePresence>
    </Container>
  );
};

export default UserDiary;

// style
const Container = styled.div`
  padding: 0 1.5rem;
`;

const SearchForm = styled.div`
  background-color: ${(props) => props.theme.textWhiteColor};
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 1.2rem;
  overflow: hidden;
  margin-top: 1.5rem;
  margin-bottom: 1.2rem;
  span {
    padding: 1.3rem;
    font-size: 1.3rem;
    font-weight: 600;
    line-height: 1.3rem;
    color: ${(props) => props.theme.textBlackColor};
  }
  .icon-box {
    display: flex;
    padding: 1.65rem;
  }
`;

const SearchConditionWrap = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: 'flex';
  justify-content: 'center';
  align-items: 'center';
  cursor: 'pointer';
  width: 100%;
  height: 100%;
  .overlay {
    display: flex;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
`;
